/**
 * A cached list of media devices to prevent multiple calls to the browser API.
 * @type {MediaDeviceInfo[] | null}
 */
let deviceCache: MediaDeviceInfo[] | null = null;

/**
 * Retrieves a list of all available media devices (microphones, speakers, cameras).
 * It caches the result for performance. To get an updated list (e.g., after plugging
 * in a new device), you must call it with forceRefresh = true.
 *
 * @param {boolean} [forceRefresh=false] - If true, it will ignore the cache and re-fetch the device list.
 * @returns {Promise<MediaDeviceInfo[]>} A promise that resolves to an array of devices.
 */
export async function getMediaDevices(forceRefresh: boolean = false): Promise<MediaDeviceInfo[]> {
  if (deviceCache && !forceRefresh) {
    return deviceCache;
  }
  if (!navigator.mediaDevices?.enumerateDevices) {
    // Media Devices API not supported.
    return [];
  }

  const devices = await navigator.mediaDevices.enumerateDevices();
  deviceCache = devices; // Update the cache
  return devices;
}

/**
 * Sets up an event listener for changes in media devices (e.g., plugging in or unplugging a headset).
 *
 * @param {(devices: MediaDeviceInfo[]) => void} callback - The function to be called when the device list changes. It receives the new list of devices as an argument.
 * @returns {() => void} A cleanup function that, when called, removes the event listener.
 */
export function listenForDeviceChanges(callback: (devices: MediaDeviceInfo[]) => void): () => void {
  const handler = async () => {
    const newDevices = await getMediaDevices(true);
    callback(newDevices);
  };

  navigator.mediaDevices.addEventListener('devicechange', handler);

  const stopListening = () => {
    navigator.mediaDevices.removeEventListener('devicechange', handler);
  };

  return stopListening;
}
